import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import CollectionSelect from 'components/application/CollectionSelect';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

import {
  computedAllScopeViewConditions,
  computedFilteredScopeViewConditions,
  computedScopedOverrideLists,
  computeVisibleOverrides,
} from 'components/helpers/resources/overrides';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function CourseSidePanelOverridesTabList({
  courseOverrides,
  hasNextPageOfCourseOverrides,
  isFetchingCourseOverrides,
  isFetchingNextPageOfCourseOverrides,
  isLoadingCourseOverrides,
  onNextCourseOverridesPageRequest,
  onOverrideRemove,
  onOverridesActiveScopeChange,
  overridesActiveScope,
  selectedRequirementPriorityIds,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelShow.overridesTab',
  });
  const { t: i18nTCommon } = useTranslation('common');

  const [endOfOverridesListRef, isEndOfOverridesListRefInView] = useInView();

  const activeScopeOptionsByValue = new Map([
    ['all', { label: i18nTCommon('shared.all'), value: 'all' }],
    ['active', { label: i18nTCommon('shared.active'), value: 'active' }],
    ['inactive', { label: i18nTCommon('shared.inactive'), value: 'inactive' }],
  ]);
  const activeScopeOptions = Array.from(activeScopeOptionsByValue.values());
  const selectedActiveScopeOption =
    activeScopeOptionsByValue.get(overridesActiveScope);

  const loadedOverrides = courseOverrides?.data || [];

  const isInitialLoadingOverrides =
    isLoadingCourseOverrides && loadedOverrides.length === 0;
  const isLoadingOverrides =
    isInitialLoadingOverrides || isFetchingNextPageOfCourseOverrides;
  const hasAnyCourseOverrides = loadedOverrides.length > 0;

  const shouldLoadMoreCourseOverrides =
    !isFetchingCourseOverrides &&
    hasNextPageOfCourseOverrides &&
    isEndOfOverridesListRefInView;
  if (shouldLoadMoreCourseOverrides) onNextCourseOverridesPageRequest();

  return (
    <div key='overridesTab' label='overrides'>
      <div className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-px-4 tw-pb-5 tw-pt-4'>
        <CollectionSelect
          customMargin='tw-mb-0'
          isSearchable={false}
          label={i18nT('filterLabel')}
          name='course-overrides-active-scope'
          onChange={(selected) => onOverridesActiveScopeChange(selected.value)}
          options={activeScopeOptions}
          value={selectedActiveScopeOption}
        />
      </div>
      {overridesActiveScope === 'all' ?
        <AllScopeOverridesList
          courseOverrides={courseOverrides}
          hasNextPageOfCourseOverrides={hasNextPageOfCourseOverrides}
          isInitialLoadingOverrides={isInitialLoadingOverrides}
          isLoadingOverrides={isLoadingOverrides}
          onOverrideRemove={onOverrideRemove}
          selectedRequirementPriorityIds={selectedRequirementPriorityIds}
        />
      : <FilteredScopeOverridesList
          courseOverrides={courseOverrides}
          hasNextPageOfCourseOverrides={hasNextPageOfCourseOverrides}
          isLoadingOverrides={isLoadingOverrides}
          onOverrideRemove={onOverrideRemove}
          overridesActiveScope={overridesActiveScope}
          selectedRequirementPriorityIds={selectedRequirementPriorityIds}
        />
      }
      {hasNextPageOfCourseOverrides &&
        hasAnyCourseOverrides &&
        !isLoadingOverrides && (
          <ul className='tw-m-0 tw-list-none tw-p-0'>
            <li
              aria-hidden='true'
              className='tw-m-0 tw-h-px tw-border-0 tw-p-0'
              ref={endOfOverridesListRef}
            />
          </ul>
        )}
    </div>
  );
}

function AllScopeOverridesList({
  courseOverrides,
  hasNextPageOfCourseOverrides,
  isInitialLoadingOverrides,
  isLoadingOverrides,
  onOverrideRemove,
  selectedRequirementPriorityIds,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelShow.overridesTab',
  });

  if (isInitialLoadingOverrides) {
    return (
      <ul className='tw-m-0 tw-list-none tw-p-0'>
        <li className='tw-sr-only'>{i18nT('loading')}</li>
        <OverridesListSection overridesActiveScope='active'>
          <ul className='tw-m-0 tw-list-none tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-0 [&>li:last-child>div]:tw-border-b-0'>
            <OverrideSkeleton />
          </ul>
        </OverridesListSection>
        <OverridesListSection overridesActiveScope='inactive'>
          <ul className='tw-m-0 tw-list-none tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-0 [&>li:last-child>div]:tw-border-b-0'>
            <OverrideSkeleton />
          </ul>
        </OverridesListSection>
      </ul>
    );
  }

  const {
    activeVisibleOverrides,
    inactiveLoadedOverrides,
    inactiveVisibleOverrides,
  } = computedScopedOverrideLists({
    overrides: courseOverrides?.data || [],
    selectedRequirementPriorityIds,
  });

  const {
    isActiveLoading,
    isInactiveLoading,
    shouldRenderActiveNoOverridesLoadedItem,
    shouldRenderInactiveNoOverridesLoadedItem,
    shouldRenderInactiveOverridesListSection,
  } = computedAllScopeViewConditions({
    activeVisibleOverridesCount: activeVisibleOverrides.length,
    hasNextPageOfCourseOverrides,
    hasSelectedOverridesForRemoval: selectedRequirementPriorityIds.length > 0,
    inactiveLoadedOverridesCount: inactiveLoadedOverrides.length,
    inactiveVisibleOverridesCount: inactiveVisibleOverrides.length,
    isLoadingOverrides,
  });

  return (
    <ul className='tw-m-0 tw-list-none tw-p-0'>
      <OverridesListSection overridesActiveScope='active'>
        <OverridesList
          courseOverrides={courseOverrides}
          isLoadingOverrides={isActiveLoading}
          keyPrefix='active'
          onOverrideRemove={onOverrideRemove}
          overridesActiveScope='active'
          shouldRenderNoOverridesLoadedItem={
            shouldRenderActiveNoOverridesLoadedItem
          }
          visibleOverrides={activeVisibleOverrides}
        />
      </OverridesListSection>
      {shouldRenderInactiveOverridesListSection && (
        <OverridesListSection overridesActiveScope='inactive'>
          <OverridesList
            courseOverrides={courseOverrides}
            isLoadingOverrides={isInactiveLoading}
            keyPrefix='inactive'
            onOverrideRemove={onOverrideRemove}
            overridesActiveScope='inactive'
            shouldRenderNoOverridesLoadedItem={
              shouldRenderInactiveNoOverridesLoadedItem
            }
            visibleOverrides={inactiveVisibleOverrides}
          />
        </OverridesListSection>
      )}
    </ul>
  );
}

function FilteredScopeOverridesList({
  courseOverrides,
  hasNextPageOfCourseOverrides,
  isLoadingOverrides,
  onOverrideRemove,
  overridesActiveScope,
  selectedRequirementPriorityIds,
}) {
  const visibleOverrides = computeVisibleOverrides({
    overrides: courseOverrides?.data || [],
    selectedRequirementPriorityIds,
  });

  const { shouldRenderNoOverridesLoadedItem } =
    computedFilteredScopeViewConditions({
      hasNextPageOfCourseOverrides,
      hasSelectedOverridesForRemoval: selectedRequirementPriorityIds.length > 0,
      isLoadingOverrides,
      visibleOverridesCount: visibleOverrides.length,
    });

  return (
    <OverridesList
      courseOverrides={courseOverrides}
      isLoadingOverrides={isLoadingOverrides}
      keyPrefix='scoped'
      onOverrideRemove={onOverrideRemove}
      overridesActiveScope={overridesActiveScope}
      shouldRenderNoOverridesLoadedItem={shouldRenderNoOverridesLoadedItem}
      visibleOverrides={visibleOverrides}
    />
  );
}

function OverridesListSection({ children, overridesActiveScope }) {
  const { t: i18nTCommon } = useTranslation('common');
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelShow.overridesTab',
  });

  return (
    <li data-element-name={`${overridesActiveScope}-overrides-list-section`}>
      <div className='tw-min-h-17.5 tw-px-5 tw-py-4'>
        <div className='tw-text-m tw-font-medium tw-tracking-wide tw-text-grey-900'>
          {i18nTCommon(`shared.${overridesActiveScope}`)}
        </div>
        <div className='tw-text-s tw-tracking-wide tw-text-grey-900'>
          {i18nT(`sections.${overridesActiveScope}.description`)}
        </div>
      </div>
      {children}
    </li>
  );
}

function OverridesList({
  courseOverrides,
  isLoadingOverrides,
  keyPrefix,
  onOverrideRemove,
  overridesActiveScope,
  shouldRenderNoOverridesLoadedItem,
  visibleOverrides,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelShow.overridesTab',
  });

  return (
    <ul className='tw-m-0 tw-list-none tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-0 [&>li:last-child>div]:tw-border-b-0'>
      {visibleOverrides.length > 0 ?
        visibleOverrides.map((override) => {
          const overrideRequirementPriority =
            courseOverrides.included
              .personnelCourseConnectionRequirementPriority[
              override.relationships
                .personnelCourseConnectionRequirementPriority.data?.id
            ];

          const overrideUserCourse =
            courseOverrides.included.userCourse[
              overrideRequirementPriority.relationships.requirementSource.data
                ?.id
            ];

          const overrideUser =
            courseOverrides.included.simpleUser[
              overrideRequirementPriority.relationships.user.data?.id
            ];

          const overridePersonnel =
            courseOverrides.included.personnel[
              overrideUserCourse?.relationships.personnel.data?.id
            ];

          const overridePersonnelProfilePhoto =
            courseOverrides.included.profilePhoto[
              overridePersonnel.relationships.profilePhoto.data?.id
            ];

          const hasOverrideBulkRemovalWriteAccess = override?.meta?.isWritable;

          return (
            <OverrideListItem
              hasOverrideBulkRemovalWriteAccess={
                hasOverrideBulkRemovalWriteAccess
              }
              key={`${keyPrefix}-override-${override.id}`}
              onOverrideRemove={onOverrideRemove}
              overridePersonnel={overridePersonnel}
              overridePersonnelProfilePhoto={overridePersonnelProfilePhoto}
              overrideRequirementPriority={overrideRequirementPriority}
              overrideUser={overrideUser}
              overrideUserCourse={overrideUserCourse}
            />
          );
        })
      : shouldRenderNoOverridesLoadedItem && (
          <OverrideListText text={i18nT(`empty.${overridesActiveScope}`)} />
        )
      }
      {isLoadingOverrides && (
        <>
          <li className='tw-sr-only'>{i18nT('loading')}</li>
          <OverrideSkeleton />
        </>
      )}
    </ul>
  );
}

function OverrideListItem({
  hasOverrideBulkRemovalWriteAccess,
  onOverrideRemove,
  overridePersonnel,
  overridePersonnelProfilePhoto,
  overrideRequirementPriority,
  overrideUser,
  overrideUserCourse,
}) {
  const { t: i18nTCommon } = useTranslation('common');
  const { hasPersonnelViewableAccess } = useTrainingRegisterResources();

  const onOverrideRemoveButtonClick = (event) => {
    event.stopPropagation();

    if (overrideRequirementPriority) {
      onOverrideRemove(overrideRequirementPriority.id);
    }
  };

  return (
    <li
      className='tw-flex hover:tw-bg-grey-025'
      data-element-name='override-list-item'
    >
      <div className='tw-mx-5 tw-inline-flex tw-w-full tw-items-end tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-py-4'>
        <div className='tw-flex tw-items-center'>
          <ProfilePhotoDisplay
            componentSize='sm'
            photoUrl={overridePersonnelProfilePhoto?.links?.croppedUrl}
            profileInitials={`${overridePersonnel.attributes.firstName?.[0] || ''}${overridePersonnel.attributes.lastName?.[0] || ''}`}
          />
        </div>
        <div className='block-list__item--text tw-w-full'>
          <div className='tw-flex'>
            <a
              className={classNames(
                'truncated-text-container truncated-text-container--width-auto tw-text-grey-700',
                hasPersonnelViewableAccess && 'hover:tw-underline',
              )}
              href={
                hasPersonnelViewableAccess && overridePersonnel ?
                  `/personnel/${overridePersonnel.id}`
                : undefined
              }
            >
              {overridePersonnel ?
                personDisplayName({
                  attributes: overridePersonnel.attributes,
                })
              : '-'}
            </a>
          </div>
          <div className='tw-between tw-mt-1 tw-flex'>
            <span className='block-list__item--sub-text-triple truncated-text-container tw-text-s tw-tracking-wide tw-text-grey-500'>
              <Trans
                i18nKey='courses.sidepanel.CourseSidePanelShow.overridesTab.row.description'
                ns='components'
                values={{
                  requirementStatus:
                    overrideUserCourse?.attributes.required ?
                      i18nTCommon('shared.requiredDowncase')
                    : i18nTCommon('shared.optionalDowncase'),
                  userName:
                    overrideUser ?
                      personDisplayName({ attributes: overrideUser.attributes })
                    : '-',
                }}
              />
            </span>
            {hasOverrideBulkRemovalWriteAccess && (
              <button
                className='tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-right tw-text-s tw-tracking-wide tw-text-red-600 hover:tw-underline'
                onClick={onOverrideRemoveButtonClick}
                type='button'
              >
                {i18nTCommon('shared.remove')}
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function OverrideListText({ text }) {
  return (
    <li data-element-name='override-list-text'>
      <div className='tw-inline-flex tw-min-h-17.5 tw-w-full tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-px-5 tw-py-4'>
        <div className='tw-self-center'>
          <span>{text}</span>
        </div>
      </div>
    </li>
  );
}

function OverrideSkeleton() {
  return (
    <li aria-hidden='true' className='tw-flex'>
      <div className='tw-mx-5 tw-inline-flex tw-h-17.5 tw-w-full tw-items-end tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-py-4'>
        <div className='tw-mr-3 tw-flex tw-items-center'>
          <div className='tw-h-8 tw-w-8 tw-animate-pulse tw-rounded-full tw-bg-grey-100' />
        </div>
        <div className='tw-flex tw-w-full tw-flex-col tw-justify-center tw-gap-2'>
          <div className='tw-h-4 tw-w-32 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
          <div className='tw-h-3 tw-w-48 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
        </div>
      </div>
    </li>
  );
}

const courseOverridesPropType = PropTypes.shape({
  data: PropTypes.arrayOf(resourceShape('override')),
  included: PropTypes.shape({
    personnelCourseConnectionRequirementPriority: PropTypes.objectOf(
      resourceShape('personnelCourseConnectionRequirementPriority'),
    ),
    userCourse: PropTypes.objectOf(resourceShape('userCourse')),
    simpleUser: PropTypes.objectOf(resourceShape('simpleUser')),
    personnel: PropTypes.objectOf(resourceShape('personnel')),
    profilePhoto: PropTypes.objectOf(resourceShape('profilePhoto')),
  }),
});

AllScopeOverridesList.propTypes = {
  courseOverrides: courseOverridesPropType,
  hasNextPageOfCourseOverrides: PropTypes.bool,
  isInitialLoadingOverrides: PropTypes.bool.isRequired,
  isLoadingOverrides: PropTypes.bool.isRequired,
  onOverrideRemove: PropTypes.func.isRequired,
  selectedRequirementPriorityIds: PropTypes.array.isRequired,
};

FilteredScopeOverridesList.propTypes = {
  courseOverrides: courseOverridesPropType,
  hasNextPageOfCourseOverrides: PropTypes.bool,
  isLoadingOverrides: PropTypes.bool.isRequired,
  onOverrideRemove: PropTypes.func.isRequired,
  overridesActiveScope: PropTypes.string.isRequired,
  selectedRequirementPriorityIds: PropTypes.array.isRequired,
};

OverridesListSection.propTypes = {
  children: PropTypes.node.isRequired,
  overridesActiveScope: PropTypes.string.isRequired,
};

OverridesList.propTypes = {
  courseOverrides: courseOverridesPropType,
  isLoadingOverrides: PropTypes.bool.isRequired,
  keyPrefix: PropTypes.string.isRequired,
  onOverrideRemove: PropTypes.func.isRequired,
  overridesActiveScope: PropTypes.string.isRequired,
  shouldRenderNoOverridesLoadedItem: PropTypes.bool.isRequired,
  visibleOverrides: PropTypes.arrayOf(resourceShape('override')).isRequired,
};

OverrideListItem.propTypes = {
  hasOverrideBulkRemovalWriteAccess: PropTypes.bool.isRequired,
  onOverrideRemove: PropTypes.func.isRequired,
  overridePersonnel: resourceShape('personnel'),
  overridePersonnelProfilePhoto: resourceShape('profilePhoto'),
  overrideRequirementPriority: resourceShape(
    'personnelCourseConnectionRequirementPriority',
  ),
  overrideUser: resourceShape('simpleUser'),
  overrideUserCourse: resourceShape('userCourse'),
};

OverrideListText.propTypes = {
  text: PropTypes.string.isRequired,
};

CourseSidePanelOverridesTabList.propTypes = {
  courseOverrides: courseOverridesPropType,
  onNextCourseOverridesPageRequest: PropTypes.func.isRequired,
  hasNextPageOfCourseOverrides: PropTypes.bool,
  isFetchingCourseOverrides: PropTypes.bool.isRequired,
  isFetchingNextPageOfCourseOverrides: PropTypes.bool.isRequired,
  isLoadingCourseOverrides: PropTypes.bool.isRequired,
  onOverrideRemove: PropTypes.func.isRequired,
  overridesActiveScope: PropTypes.string.isRequired,
  selectedRequirementPriorityIds: PropTypes.array.isRequired,
  onOverridesActiveScopeChange: PropTypes.func.isRequired,
};
